import { createContext, useCallback, useContext, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";

import { createApiClient } from "common/apiClient";

const ApiClientContext = createContext(null);

function ApiClientProvider({ children }) {
  const { getToken } = useKindeAuth();
  const [errorMessage, setErrorMessage] = useState("");
  const reportError = useCallback((error) => {
    setErrorMessage(error?.message || "Unable to complete the request. Please try again.");
  }, []);
  const apiClient = useMemo(
    () => createApiClient({ getToken, onError: reportError }),
    [getToken, reportError]
  );

  return (
    <ApiClientContext.Provider value={apiClient}>
      {children}
      <Snackbar
        open={Boolean(errorMessage)}
        autoHideDuration={8000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setErrorMessage("")}
      >
        <Alert severity="error" variant="filled" onClose={() => setErrorMessage("")}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </ApiClientContext.Provider>
  );
}

ApiClientProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

function useApiClient() {
  const apiClient = useContext(ApiClientContext);

  if (!apiClient) {
    throw new Error("useApiClient must be used within ApiClientProvider.");
  }

  return apiClient;
}

export { ApiClientProvider, useApiClient };
